import React, { useState, useCallback, useImperativeHandle, forwardRef, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { validateAndParseJSON, formatJSON, findJSONPath, JSONValidationResult, HighlightRange } from '../utils/jsonHighlightUtils';
import { usePlatform } from '../hooks/usePlatform';
import { readFromClipboard } from '../utils/clipboardUtils';
import { importFile, validateAndParseImportedJSON, exportToFile } from '../utils/fileUtils';
import { getInteractionClasses } from '../utils/platformUtils';

interface JsonInputProps {
  onJsonChange: (json: any) => void;
  onError: (error: string | null) => void;
  selectedPath?: string | null;
  enableJSON5?: boolean;
}

export interface JsonInputRef {
  reset: () => void;
  formatJSON: () => void;
  importFromFile: () => void;
  pasteFromClipboard: () => void;
  exportToFile: () => void;
}

const JsonInput = forwardRef<JsonInputRef, JsonInputProps>(({ 
  onJsonChange, 
  onError, 
  selectedPath = null,
  enableJSON5 = false 
}, ref) => {
  const { t } = useTranslation();
  const platform = usePlatform();
  const [jsonText, setJsonText] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [validation, setValidation] = useState<JSONValidationResult | null>(null);
  const [highlightRange, setHighlightRange] = useState<HighlightRange | null>(null);
  const [isMinified, setIsMinified] = useState(false);
  const [originalJson, setOriginalJson] = useState('');
  const [isImporting, setIsImporting] = useState(false);
  const [importStatus, setImportStatus] = useState<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useImperativeHandle(ref, () => ({
    reset: () => {
      setJsonText('');
      setError(null);
      setValidation(null);
      setHighlightRange(null);
      setIsMinified(false);
      setOriginalJson('');
      setImportStatus(null);
      onError(null);
      onJsonChange(null);
    },
    formatJSON: () => {
      if (jsonText.trim()) {
        try {
          const formatted = formatJSON(jsonText, enableJSON5);
          setJsonText(formatted);
          setIsMinified(false);
          validateAndUpdate(formatted);
        } catch (error) {
          // Format failed, keep original text
        }
      }
    },
    importFromFile: async () => {
      await handleFileImport();
    },
    pasteFromClipboard: async () => {
      await handlePasteFromClipboard();
    },
    exportToFile: () => {
      handleExportToFile();
    }
  }));

  const validateAndUpdate = useCallback((value: string) => {
    if (value.trim() === '') {
      setError(null);
      setValidation(null);
      setHighlightRange(null);
      onError(null);
      onJsonChange(null);
      return;
    }

    const validationResult = validateAndParseJSON(value, enableJSON5);
    setValidation(validationResult);

    if (validationResult.isValid) {
      setError(null);
      onError(null);
      onJsonChange(validationResult.parsed);
    } else {
      setError(validationResult.error || 'Invalid JSON');
      onError(validationResult.error || 'Invalid JSON');
      onJsonChange(null);
    }
  }, [onJsonChange, onError, enableJSON5]);

  const handleInputChange = useCallback((event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = event.target.value;
    setJsonText(value);
    // Store original when user starts typing
    if (originalJson === '' && value.trim() !== '') {
      setOriginalJson(value);
    }
    validateAndUpdate(value);
  }, [validateAndUpdate, originalJson]);

  const toggleMinifyPretty = () => {
    if (!jsonText.trim() || error) return;
    
    try {
      if (isMinified) {
        // Pretty print
        const formatted = formatJSON(jsonText, enableJSON5);
        setJsonText(formatted);
        setIsMinified(false);
        validateAndUpdate(formatted);
      } else {
        // Minify
        const validationResult = validateAndParseJSON(jsonText, enableJSON5);
        if (validationResult.isValid) {
          const minified = JSON.stringify(validationResult.parsed);
          setJsonText(minified);
          setIsMinified(true);
          validateAndUpdate(minified);
        }
      }
    } catch (error) {
      // Failed to format/minify
    }
  };

  // File import handler
  const handleFileImport = async () => {
    setIsImporting(true);
    setImportStatus(null);
    
    try {
      const result = await importFile(platform);
      
      if (result.success && result.content) {
        const validation = validateAndParseImportedJSON(result.content);
        
        if (validation.isValid) {
          setJsonText(result.content);
          validateAndUpdate(result.content);
          setImportStatus(`Successfully imported ${result.fileName}`);
        } else {
          setImportStatus(`Invalid JSON in ${result.fileName}: ${validation.error}`);
        }
      } else {
        setImportStatus(result.error || 'Failed to import file');
      }
    } catch (error) {
      setImportStatus('Failed to import file');
    } finally {
      setIsImporting(false);
      // Clear status after 3 seconds
      setTimeout(() => setImportStatus(null), 3000);
    }
  };

  // Clipboard paste handler
  const handlePasteFromClipboard = async () => {
    try {
      const result = await readFromClipboard(platform);
      
      if (result.success && result.data) {
        const validation = validateAndParseImportedJSON(result.data);
        
        if (validation.isValid) {
          setJsonText(result.data);
          validateAndUpdate(result.data);
          setImportStatus('Successfully pasted from clipboard');
        } else {
          setImportStatus(`Invalid JSON from clipboard: ${validation.error}`);
        }
      } else {
        setImportStatus(result.error || 'Failed to read from clipboard');
      }
    } catch (error) {
      setImportStatus('Failed to read from clipboard');
    } finally {
      // Clear status after 3 seconds
      setTimeout(() => setImportStatus(null), 3000);
    }
  };

  // Export handler
  const handleExportToFile = () => {
    if (!jsonText.trim()) {
      setImportStatus('No content to export');
      setTimeout(() => setImportStatus(null), 3000);
      return;
    }

    try {
      const fileName = `json-export-${new Date().toISOString().split('T')[0]}.json`;
      exportToFile(jsonText, fileName, platform);
      setImportStatus('File exported successfully');
    } catch (error) {
      setImportStatus('Failed to export file');
    } finally {
      setTimeout(() => setImportStatus(null), 3000);
    }
  };

  // Update highlighting when selectedPath changes
  useEffect(() => {
    if (selectedPath && jsonText && validation?.isValid) {
      const range = findJSONPath(jsonText, selectedPath);
      setHighlightRange(range);
      
      // Scroll to highlighted section
      if (range && textareaRef.current) {
        const lineHeight = 20; // Approximate line height
        const scrollTop = (range.line - 1) * lineHeight;
        const nextScrollTop = Math.max(0, scrollTop - 100);
        textareaRef.current.scrollTop = nextScrollTop;
      }
    } else {
      setHighlightRange(null);
    }
  }, [selectedPath, jsonText, validation]);

  const renderTextarea = () => {
    return (
      <textarea
        ref={textareaRef}
        value={jsonText}
        onChange={handleInputChange}
        placeholder={t('jsonInput.placeholder')}
        wrap="off"
        className={`code-surface scrollbar-thin flex-1 w-full p-3 sm:p-4 border rounded-xl font-code text-xs sm:text-sm resize-none focus:outline-none focus:ring-2 transition-all duration-200 text-on-surface-variant placeholder:text-outline/60 ${
          error 
            ? 'border-error focus:ring-error/30 focus:border-error' 
            : 'border-white/10 focus:ring-primary-container/20 focus:border-primary-container'
        }`}
      />
    );
  };

  return (
    <div className="relative z-10 flex h-full flex-col">
      <div className="mb-4 border-b border-white/10 pb-4">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3">
          <div className="flex-1">
            <p className="font-code text-[11px] uppercase tracking-[0.28em] text-primary-container/70">Source</p>
            <h2 className="font-display text-2xl font-semibold tracking-[-0.03em] text-on-surface">{t('jsonInput.title')}</h2>
            <p className="mt-1 text-sm text-on-surface-variant">
              {enableJSON5 ? t('jsonInput.subtitleJson5') : t('jsonInput.subtitle')}
            </p>
          </div>
          
          <div className="flex flex-wrap gap-2 sm:gap-2">
            {/* Import/Export Buttons - Always visible */}
            <button
              onClick={handleFileImport}
              disabled={isImporting}
              className={`rounded-lg border border-secondary/45 bg-secondary/10 px-3 py-2 text-xs font-semibold text-secondary shadow-[0_0_14px_rgba(236,177,255,0.12)] transition hover:bg-secondary hover:text-on-secondary ${getInteractionClasses(platform)} disabled:opacity-50 disabled:cursor-not-allowed`}
              title="Import JSON file"
              style={{ minHeight: platform.isMobile ? '44px' : '32px' }}
            >
              <span className="hidden sm:inline">{isImporting ? 'Importing...' : 'Import'}</span>
              <span className="sm:hidden">📁</span>
            </button>
            
            <button
              onClick={handlePasteFromClipboard}
              className={`rounded-lg border border-tertiary-container/55 bg-tertiary-container/10 px-3 py-2 text-xs font-semibold text-tertiary-container shadow-[0_0_14px_rgba(254,216,58,0.12)] transition hover:bg-tertiary-container hover:text-on-tertiary ${getInteractionClasses(platform)}`}
              title="Paste from clipboard"
              style={{ minHeight: platform.isMobile ? '44px' : '32px' }}
            >
              <span className="hidden sm:inline">Paste</span>
              <span className="sm:hidden">📋</span>
            </button>

            {jsonText && (
              <>
                <button
                  onClick={handleExportToFile}
                  className={`rounded-lg border border-primary-container/45 bg-primary-container/10 px-3 py-2 text-xs font-semibold text-primary-container transition hover:bg-primary-container hover:text-on-primary ${getInteractionClasses(platform)}`}
                  title="Export to file"
                  style={{ minHeight: platform.isMobile ? '44px' : '32px' }}
                >
                  <span className="hidden sm:inline">Export</span>
                  <span className="sm:hidden">💾</span>
                </button>

                <button
                  onClick={toggleMinifyPretty}
                  disabled={!!error}
                  className={`rounded-lg border border-secondary/45 bg-secondary/10 px-3 py-2 text-xs font-semibold text-secondary transition hover:bg-secondary hover:text-on-secondary ${getInteractionClasses(platform)} disabled:opacity-50 disabled:cursor-not-allowed`}
                  title={isMinified ? 'Pretty Print' : 'Minify'}
                  style={{ minHeight: platform.isMobile ? '44px' : '32px' }}
                >
                  {isMinified ? '📖' : '🗜️'}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
      
      <div className="flex-1 flex flex-col min-h-0">
        {renderTextarea()}
        
        {validation && !validation.isValid && (
          <div className="mt-4 p-4 bg-error-container/20 border border-error/35 rounded-xl shadow-[0_0_18px_rgba(255,180,171,0.10)]">
            <div className="flex justify-between items-start mb-2">
              <p className="text-sm text-error font-body">
                <span className="font-medium">{t('errors.jsonError')}</span> {validation.error}
              </p>
              {validation.line && validation.column && (
                <span className="text-xs text-error font-code">
                  {t('selectedPath.line')} {validation.line}:{validation.column}
                </span>
              )}
            </div>
            {validation.suggestion && (
              <p className="text-xs text-error font-body mt-2">
                💡 <span className="font-medium">{t('errors.suggestion')}</span> {validation.suggestion}
              </p>
            )}
          </div>
        )}
        
        {importStatus && (
          <div className={`mt-2 p-2 rounded-xl border ${
            importStatus.includes('Successfully') || importStatus.includes('exported')
              ? 'bg-primary-container/10 border-primary-container/25'
              : 'bg-tertiary-container/10 border-tertiary-container/25'
          }`}>
            <p className={`text-xs font-primary ${
              importStatus.includes('Successfully') || importStatus.includes('exported')
                ? 'text-primary-container'
                : 'text-tertiary-container'
            }`}>
              <span className="font-medium">
                {importStatus.includes('Successfully') || importStatus.includes('exported') ? '✅' : '⚠️'}
              </span> {importStatus}
            </p>
          </div>
        )}

        {selectedPath && highlightRange && (
          <div className="mt-2 p-2 bg-tertiary-container/10 border border-tertiary-container/25 rounded-xl">
            <p className="text-xs text-tertiary-container font-body">
              <span className="font-medium">{t('selectedPath.highlighted')}</span> {selectedPath} 
              <span className="ml-2 text-tertiary-container/70">
                ({t('selectedPath.line')} {highlightRange.line})
              </span>
            </p>
          </div>
        )}
      </div>
    </div>
  );
});

JsonInput.displayName = 'JsonInput';

export default JsonInput;